import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { IMG } from "@/lib/imagery";
import api from "@/lib/api";
import Captcha from "@/components/Captcha";
import { useHoneypot } from "@/components/Honeypot";

export default function Login() {
  const { login, telegramWidgetLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mfaMode, setMfaMode] = useState(false);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [captchaMode, setCaptchaMode] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const hp = useHoneypot();

  const redirect = location.state?.from?.pathname || "/dashboard";

  const tgRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/auth/telegram/bot").then(({ data }) => {
      if (cancelled || !data.username || !tgRef.current) return;
      window.onTelegramAuth = async (u) => {
        try {
          const usr = await telegramWidgetLogin(u);
          toast.success(`Welcome back, ${usr.full_name.split(" ")[0]}`);
          navigate(usr.role === "admin" ? "/admin" : redirect, { replace: true });
        } catch (err) { toast.error(err?.response?.data?.detail || "Telegram login failed"); }
      };
      const s = document.createElement("script");
      s.src = "https://telegram.org/js/telegram-widget.js?22";
      s.async = true;
      s.setAttribute("data-telegram-login", data.username);
      s.setAttribute("data-size", "large");
      s.setAttribute("data-userpic", "false");
      s.setAttribute("data-request-access", "write");
      s.setAttribute("data-onauth", "onTelegramAuth(user)");
      tgRef.current.appendChild(s);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const extra = mfaMode ? (useRecovery ? { recovery_code: code } : { otp: code }) : {};
      if (captcha) { extra.captcha_token = captcha.token; extra.captcha_answer = captcha.answer; }
      Object.assign(extra, hp.values());
      const u = await login(email, password, extra);
      if (u?.captcha_required) {
        setCaptchaMode(true);
        toast.info("Please complete the captcha to continue");
        return;
      }
      if (u?.mfa_required) {
        setMfaMode(true);
        toast.info("Two-factor required — enter your authenticator code");
        return;
      }
      toast.success(`Welcome back, ${u.full_name.split(" ")[0]}`);
      navigate(u.role === "admin" ? "/admin" : redirect, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      side={
        <>
          <p className="font-editorial italic text-[color:var(--ink-muted)]">
            Access · Members
          </p>
          <h1 className="mt-6 font-editorial text-5xl font-normal leading-[1.05] tracking-tight text-[color:var(--ink)] sm:text-6xl">
            Welcome back to the desk.
          </h1>
          <p className="mt-6 max-w-md text-[16px] leading-relaxed text-[color:var(--ink-body)]">
            Your licenses, subscriptions, and course lessons wait behind this
            gate. Bind an MT4 or MT5 account once — activate everywhere.
          </p>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid gap-5" data-testid="login-form">
        {hp.field}
        <FormField label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@edge.trade"
            data-testid="login-email"
            className="w-full rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 text-[15px] text-[color:var(--ink)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]"
          />
        </FormField>
        <FormField label="Password">
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="login-password"
              className="w-full rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 pr-12 text-[15px] text-[color:var(--ink)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
              aria-label="Toggle password"
              data-testid="login-toggle-password"
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </FormField>
        {mfaMode && (
          <FormField label={useRecovery ? "Recovery code" : "Authenticator code"}>
            <input
              type="text"
              inputMode={useRecovery ? "text" : "numeric"}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={useRecovery ? "XXXXX-XXXXX" : "123456"}
              data-testid="login-2fa-code"
              className="w-full rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 text-[15px] text-[color:var(--ink)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]"
            />
            <button type="button" onClick={() => { setUseRecovery((v) => !v); setCode(""); }} data-testid="login-2fa-toggle-recovery"
              className="mt-2 text-[13px] text-[color:var(--evergreen)] hover:underline">
              {useRecovery ? "Use an authenticator code instead" : "Use a recovery code instead"}
            </button>
          </FormField>
        )}
        {captchaMode && (
          <div data-testid="login-captcha">
            <p className="mb-2 text-[13px] text-[color:var(--ink-body)]">Quick check to keep bots out:</p>
            <Captcha onChange={setCaptcha} />
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit"
          className="btn-edge mt-2 w-full justify-center disabled:cursor-wait disabled:opacity-70"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <>{mfaMode ? "Verify & sign in" : "Sign in"} <ArrowRight size={16} /></>}
        </button>
        <div className="mt-2 flex flex-col items-center gap-1 text-center text-[14px] text-[color:var(--ink-muted)]">
          <Link to="/forgot-password" className="font-medium text-[color:var(--evergreen)] hover:underline" data-testid="login-link-forgot">
            Forgot your password?
          </Link>
          <span>
            New here?{" "}
            <Link to="/register" className="font-medium text-[color:var(--evergreen)] hover:underline" data-testid="login-link-register">
              Create an account
            </Link>
          </span>
        </div>
        <div className="mt-5">
          <div className="mb-3 flex items-center gap-3 text-[13px] text-[color:var(--ink-muted)]">
            <span className="h-px flex-1 bg-[color:var(--ink-hairline)]" /> or <span className="h-px flex-1 bg-[color:var(--ink-hairline)]" />
          </div>
          <div ref={tgRef} className="flex justify-center" data-testid="telegram-login-widget" />
          <p className="mt-2 text-center text-[12px] text-[color:var(--ink-muted)]">Sign in with Telegram works once you've linked it in Dashboard → Security.</p>
        </div>
      </form>
    </AuthShell>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="mb-2 block font-editorial italic text-[14px] text-[color:var(--ink-muted)]">
        {label}
      </label>
      {children}
    </div>
  );
}

export function AuthShell({ side, children }) {
  return (
    <section className="relative grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      {/* Editorial panel */}
      <div className="relative hidden overflow-hidden bg-[color:var(--paper-soft)] lg:block">
        <div className="relative flex h-full min-h-[720px] flex-col justify-between p-12 xl:p-20">
          <div>{side}</div>
          <div>
            <div className="photo-vignette h-48 w-full max-w-md">
              <img src={IMG.forest} alt="" className="h-full w-full object-cover" />
            </div>
            <p className="mt-4 max-w-md font-editorial italic text-[color:var(--ink-muted)]">
              — The desk, remote, global
            </p>
          </div>
        </div>
      </div>
      {/* Form panel */}
      <div className="relative flex items-center justify-center bg-[color:var(--paper)] px-5 py-14 sm:px-8 lg:py-20">
        <div className="relative w-full max-w-md">
          <div className="lg:hidden">
            <div className="mb-8">{side}</div>
          </div>
          <div className="rounded-lg border border-[color:var(--ink-hairline)] bg-white p-8 lg:p-10">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}
